import type { MouseEvent } from 'react';
import Button from '@/components/base/Button';
import { PriorityBadge, SourceBadge, StatusBadge } from '@/components/base/Badges';
import type { Order } from '@/mocks/orders';
import type { OrderActionHandlers } from '@/pages/orders/types';

interface OrdersTableProps extends OrderActionHandlers {
  orders: Order[];
  loading: boolean;
  flashId: string;
  onOpenOrder: (order: Order) => void;
}

function RowActions({
  order,
  handlers,
}: {
  order: Order;
  handlers: OrderActionHandlers;
}) {
  const stop = (event: MouseEvent) => event.stopPropagation();

  if (order.status === 'pending') {
    return (
      <div className="flex items-center gap-2" onClick={stop}>
        <Button size="sm" variant="cta" icon="ri-check-line" onClick={() => handlers.onAccept(order.id)}>
          قبول
        </Button>
        <Button size="sm" variant="danger" onClick={() => handlers.onReject(order.id)}>
          رفض
        </Button>
      </div>
    );
  }

  if (order.status === 'accepted') {
    return (
      <div onClick={stop}>
        <Button size="sm" variant="primary" icon="ri-play-line" onClick={() => handlers.onStart(order.id)}>
          بدء التنفيذ
        </Button>
      </div>
    );
  }

  if (order.status === 'processing') {
    return (
      <div onClick={stop}>
        <Button size="sm" variant="primary" icon="ri-checkbox-circle-line" onClick={() => handlers.onReady(order.id)}>
          جاهز للتسليم
        </Button>
      </div>
    );
  }

  if (order.status === 'needs_driver') {
    return (
      <div onClick={stop}>
        <Button size="sm" variant="cta" icon="ri-e-bike-2-line" onClick={() => handlers.onAssign(order.id)}>
          عيّن مندوب
        </Button>
      </div>
    );
  }

  if (order.status === 'ready') {
    return (
      <div onClick={stop}>
        <Button size="sm" variant="soft" icon="ri-hand-heart-line" onClick={() => handlers.onComplete(order.id)}>
          تأكيد التسليم
        </Button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={stop}
      className="flex h-8 items-center gap-1 rounded-md px-2 text-[13px] font-medium text-secondary-600 transition-colors hover:bg-secondary-50"
    >
      <i className="ri-eye-line text-base" aria-hidden="true"></i>
      عرض
    </button>
  );
}

export default function OrdersTable({
  orders,
  loading,
  flashId,
  onOpenOrder,
  ...handlers
}: OrdersTableProps) {
  const headers = [
    'رقم الطلب',
    'العميل',
    'القطع',
    'نوع الخدمة',
    'الأولوية',
    'طريقة التوصيل',
    'المبلغ',
    'الحالة',
    'التاريخ',
    'إجراء',
  ];

  return (
    <div className="overflow-hidden rounded-card border border-background-200 bg-white shadow-card">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1100px] border-collapse text-right">
          <thead>
            <tr className="bg-background-50">
              {headers.map((header) => (
                <th
                  key={header}
                  className="whitespace-nowrap px-4 py-3 font-semibold text-[12px] text-foreground-500"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: 6 }).map((_, index) => (
                  <tr key={index} className="border-t border-background-100">
                    {headers.map((header) => (
                      <td key={header} className="px-4 py-4">
                        <div className="h-4 w-full max-w-[110px] animate-pulse rounded bg-background-200"></div>
                      </td>
                    ))}
                  </tr>
                ))
              : orders.map((order) => (
                  <tr
                    key={order.id}
                    onClick={() => onOpenOrder(order)}
                    className={[
                      'cursor-pointer border-t border-background-100 transition-colors hover:bg-background-100',
                      flashId === order.id ? 'dc-flash' : '',
                    ].join(' ')}
                  >
                    <td className="px-4 py-3.5">
                      <span className="block font-semibold text-[13px] text-secondary-600">
                        {order.orderNo}
                      </span>
                      <span className="mt-1 block">
                        <SourceBadge source={order.source} />
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="block text-[13px] font-medium text-foreground-900">
                        {order.customer}
                      </span>
                      <span className="mt-0.5 block text-[11px] text-foreground-400" dir="ltr">
                        {order.phone}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-[13px] text-foreground-700">
                      {order.piecesCount} قطعة
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5 text-[13px] text-foreground-700">
                      {order.serviceType}
                    </td>
                    <td className="px-4 py-3.5">
                      <PriorityBadge priority={order.priority} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5 text-[13px] text-foreground-700">
                      {order.deliveryMethod}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5 text-[13px] font-semibold text-foreground-950">
                      {order.amount} ر.س
                    </td>
                    <td className="px-4 py-3.5">
                      <StatusBadge status={order.status} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5">
                      <span className="block text-[12px] text-foreground-600">{order.date}</span>
                      <span className="mt-0.5 block text-[11px] text-foreground-400">{order.time}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <RowActions order={order} handlers={handlers} />
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}